import React from "react";
import { Container, Badge } from "react-bootstrap";
import { Helmet } from "react-helmet";
import AppNavbar from "../../../components/common/AppNavbar";

const IndiaForeignerRegistration = () => {
  const statCards = [
    {
      label: "Old grace window",
      value: "14 days",
      delta: "Now removed",
      deltaColor: "#dc2626",
    },
    {
      label: "Registration deadline",
      value: "180 days",
      delta: "Must register before",
      deltaColor: "#1d6a2e",
    },
    {
      label: "Birth notification",
      value: "30 days",
      delta: "Waived if parent is citizen",
      deltaColor: "#1d6a2e",
    },
    {
      label: "Authority",
      value: "FRRO / FRO",
      delta: "Official registrar",
      deltaColor: "#1d6a2e",
    },
  ];

  const timelineItems = [
    {
      label: "Before the amendment",
      text:
        "Foreign nationals had a 14-day window after crossing 180 days to register with the FRRO or FRO.",
    },
    {
      label: "Gazette notification (June 2026)",
      text:
        "MHA publishes amended Immigration and Foreigners Rules, 2025 — 14-day grace period removed.",
    },
    {
      label: "Effective immediately",
      text:
        "All foreigners intending to stay beyond 180 days must now register before the period expires — no exceptions except genuine emergencies.",
    },
  ];

  return (
    <>
      {/* ══════════════════════════════════════════════
          SEO — HELMET
      ══════════════════════════════════════════════ */}
      <Helmet>
        <html lang="en" />
        <title>
          Register Before 180 Days or Leave: India Tightens Foreigner
          Registration Rules | Bharat Abroad
        </title>
        <meta
          name="description"
          content="India's MHA has removed the 14-day grace period for foreigner registration. Foreign nationals must now register with FRRO or FRO before their 180-day stay expires. Full breakdown of the amended Immigration and Foreigners Rules, 2025."
        />
        <meta
          name="keywords"
          content="India foreigner registration rules 2025, FRRO registration 180 days, MHA immigration amendment, foreigner registration India deadline, India visa overstay rules, FRRO FRO registration, immigration India 2026, Indian immigration policy update"
        />
        <meta name="author" content="Bharat Abroad Editorial" />
        <meta
          name="robots"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
        <meta name="theme-color" content="#0a1a2e" />
        <link
          rel="canonical"
          href="https://www.bharatabroad.com/articles/india-foreigner-registration-180-days-2026"
        />

        {/* ── Geo / Audience ── */}
        <meta name="geo.region" content="IN" />
        <meta name="geo.placename" content="India" />
        <meta
          name="audience"
          content="Indians abroad, NRI, foreign nationals in India, expats, international students"
        />

        {/* ── Open Graph ── */}
        <meta property="og:type" content="article" />
        <meta
          property="og:title"
          content="Register Before 180 Days or Leave: India Tightens Foreigner Registration Rules | Bharat Abroad"
        />
        <meta
          property="og:description"
          content="India's MHA has removed the 14-day grace period for foreigner registration. Foreign nationals must now register with FRRO or FRO before their 180-day stay expires."
        />
        <meta
          property="og:url"
          content="https://www.bharatabroad.com/articles/india-foreigner-registration-180-days-2026"
        />
        <meta property="og:site_name" content="Bharat Abroad" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content="India tightens foreigner registration rules — 180-day deadline"
        />
        <meta
          property="article:published_time"
          content="2026-06-09T00:00:00+05:30"
        />
        <meta
          property="article:modified_time"
          content="2026-06-09T00:00:00+05:30"
        />
        <meta
          property="article:author"
          content="https://www.bharatabroad.com/about"
        />
        <meta
          property="article:publisher"
          content="https://www.bharatabroad.com"
        />
        <meta property="article:section" content="Immigration &amp; Policy" />
        <meta property="article:tag" content="India Immigration" />
        <meta property="article:tag" content="FRRO Registration" />
        <meta property="article:tag" content="MHA Policy" />
        <meta property="article:tag" content="Foreigner Rules India" />
        <meta property="article:tag" content="180 Day Visa Rule" />

        {/* ── Twitter Card ── */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@BharatAbroad" />
        <meta name="twitter:creator" content="@BharatAbroad" />
        <meta
          name="twitter:title"
          content="Register Before 180 Days or Leave: India Tightens Foreigner Registration Rules – Bharat Abroad"
        />
        <meta
          name="twitter:description"
          content="India removes 14-day grace period for FRRO registration. Foreign nationals must now register before the 180-day stay deadline. Full policy breakdown."
        />
        <meta
          name="twitter:image:alt"
          content="India Foreigner Registration Rules 2026 — Bharat Abroad"
        />

        {/* ── JSON-LD: Article ── */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Register Before 180 Days or Leave: India Tightens Foreigner Registration Rules",
            "description": "India's MHA has amended the Immigration and Foreigners Rules, 2025, removing the 14-day grace period. Foreign nationals must now register with FRRO or FRO before the 180-day stay period expires.",
            "datePublished": "2026-06-09T00:00:00+05:30",
            "dateModified": "2026-06-09T00:00:00+05:30",
            "author": {
              "@type": "Organization",
              "name": "Bharat Abroad Editorial",
              "url": "https://www.bharatabroad.com/about"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Bharat Abroad",
              "url": "https://www.bharatabroad.com"
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://www.bharatabroad.com/articles/india-foreigner-registration-180-days-2026"
            },
            "articleSection": "Immigration & Policy",
            "keywords": "India foreigner registration, FRRO, 180 days, MHA amendment, Immigration Foreigners Rules 2025",
            "wordCount": "700",
            "timeRequired": "PT5M",
            "inLanguage": "en-IN",
            "about": [
              { "@type": "Thing", "name": "Immigration Policy" },
              { "@type": "Thing", "name": "Foreigner Registration" },
              { "@type": "Thing", "name": "FRRO India" },
              { "@type": "Thing", "name": "Ministry of Home Affairs" }
            ],
            "audience": {
              "@type": "Audience",
              "audienceType": "Foreign nationals residing in India, NRI families, international students and professionals"
            }
          }
        `}</script>

        {/* ── JSON-LD: BreadcrumbList ── */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.bharatabroad.com" },
              { "@type": "ListItem", "position": 2, "name": "Immigration & Policy", "item": "https://www.bharatabroad.com/immigration-policy" },
              { "@type": "ListItem", "position": 3, "name": "India Foreigner Registration 180 Days 2026", "item": "https://www.bharatabroad.com/articles/india-foreigner-registration-180-days-2026" }
            ]
          }
        `}</script>

        {/* ── JSON-LD: FAQPage ── */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is the new foreigner registration deadline in India?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Under the amended Immigration and Foreigners Rules, 2025, foreign nationals intending to stay beyond 180 days must register with the FRRO or FRO before the 180-day period expires. The earlier 14-day grace period has been removed."
                }
              },
              {
                "@type": "Question",
                "name": "Who does the new 180-day registration rule apply to?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The rule applies to foreign nationals holding visas valid for 180 days or less who plan to stay longer, and to those holding longer-validity visas that carry a per-stay condition limiting each visit to 180 days."
                }
              },
              {
                "@type": "Question",
                "name": "What is the FRRO and how do I register?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The Foreigners Regional Registration Office (FRRO) and Foreigners Registration Office (FRO) are the official bodies for foreigner registration in India. Registration can be done online through the e-FRRO portal or in person at regional offices."
                }
              },
              {
                "@type": "Question",
                "name": "Are there any exceptions to the new 180-day rule?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, but very limited. The government will consider requests to stay beyond the permitted period only in exceptional or emergency circumstances. Routine extension requests will not be accommodated."
                }
              },
              {
                "@type": "Question",
                "name": "What changed for children born in India to foreign parents?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The new rules waive the 30-day electronic notification requirement if at least one parent is an Indian citizen intending to retain the child's Indian citizenship. However, if the child later acquires foreign citizenship while in India, the parent must notify the registration officer within 30 days."
                }
              }
            ]
          }
        `}</script>

        {/* ── JSON-LD: WebSite ── */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Bharat Abroad",
            "url": "https://www.bharatabroad.com",
            "potentialAction": {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://www.bharatabroad.com/search?q={search_term_string}"
              },
              "query-input": "required name=search_term_string"
            }
          }
        `}</script>
      </Helmet>

      <AppNavbar />

      <div style={{ background: "#f4f7fb" }}>
        {/* ══════════════════════════════
            HERO SECTION
        ══════════════════════════════ */}
        <div
          style={{
            background: "linear-gradient(135deg, #0a1a2e, #1a2d4a, #0d1f0d)",
            color: "white",
            padding: "60px 0",
          }}
        >
          <Container>
            <Badge bg="danger" className="mb-3">
              📋 Immigration · Policy Update · June 2026
            </Badge>
            <h1 className="fw-bold">
              Register Before 180 Days or Leave: India Tightens Foreigner
              Registration Rules
            </h1>
            <p className="lead mt-3" style={{ maxWidth: "900px" }}>
              The Union Ministry of Home Affairs has amended the Immigration and
              Foreigners Rules, 2025 — removing the 14-day grace period and
              requiring foreign nationals to register with FRRO or FRO before
              their 180-day stay expires.
            </p>
          </Container>
        </div>

        {/* ══════════════════════════════
            BODY
        ══════════════════════════════ */}
        <section>
          <div className="container py-5">
            <p>
              New Delhi: The Union Ministry of Home Affairs (MHA) has amended
              the Immigration and Foreigners Rules, 2025, removing the 14-day
              grace period that previously allowed certain foreign nationals to
              register after completing 180 days of stay in India.
            </p>
            <p>
              The revised rules, notified through an official gazette
              notification on Monday, require foreigners intending to stay
              beyond 180 days to complete their registration formalities before
              the expiry of that period.
            </p>

            {/* ── Stat Cards ── */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: "12px",
                margin: "24px 0",
              }}
            >
              {statCards.map((s) => (
                <div
                  key={s.label}
                  style={{
                    background: "#fff",
                    border: "1px solid #dde2ea",
                    borderRadius: "8px",
                    padding: "16px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "11px",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      color: "#999",
                      marginBottom: "6px",
                      fontWeight: 600,
                    }}
                  >
                    {s.label}
                  </div>
                  <div
                    style={{
                      fontSize: "22px",
                      fontWeight: 700,
                      color: "#0a1a2e",
                      lineHeight: 1.2,
                    }}
                  >
                    {s.value}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: s.deltaColor,
                      fontWeight: 600,
                      marginTop: "3px",
                    }}
                  >
                    {s.delta}
                  </div>
                </div>
              ))}
            </div>

            <hr />

            {/* ── Section 1 ── */}
            <h3>Registration Deadline Moved Forward</h3>

            <h4>What changed</h4>
            <p>
              Under the earlier provisions, eligible foreign nationals could
              register with the Foreigners Regional Registration Office (FRRO)
              or Foreigners Registration Office (FRO) within 14 days after
              crossing the 180-day stay limit. With the latest amendment,
              registration must now be completed any time before the 180-day
              period expires.
            </p>

            <h4>Who is affected</h4>
            <ul>
              <li>
                Foreign nationals holding visas valid for 180 days or less who
                plan to remain in India beyond that period
              </li>
              <li>
                Foreign nationals holding visas valid for more than 180 days but
                carrying a condition that limits each stay to a maximum of 180
                days
              </li>
            </ul>

            {/* ── Image 1 ── */}
            <img
              src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRbCO3BNKwCn3OHm_MiL4eRS9XLtT_an5g-uQ&s"
              alt="Foreigners Regional Registration Office India"
              onError={(e) => {
                e.target.src =
                  "https://images.unsplash.com/photo-1569974507005-6dc61f97fb5c?w=800&q=80";
                e.target.onerror = null;
              }}
              style={{
                width: "100%",
                height: "260px",
                objectFit: "cover",
                borderRadius: "10px",
                margin: "20px 0 8px",
                border: "1px solid #dde2ea",
                display: "block",
              }}
            />
            <p
              style={{
                fontSize: "12px",
                color: "#999",
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              Foreigners Regional Registration Office (FRRO) — the official
              authority for foreigner registration in India
            </p>

            {/* Callout */}
            <div
              style={{
                borderLeft: "4px solid #1a3a1a",
                background: "#f0f7f0",
                borderRadius: "0 8px 8px 0",
                padding: "14px 20px",
                margin: "20px 0",
                fontSize: "15px",
                color: "#1a3a1a",
                lineHeight: 1.7,
              }}
            >
              <strong>Key note:</strong> The government has clarified that
              requests to remain beyond the permitted stay period will be
              approved only in exceptional or emergency circumstances. Routine
              extensions will no longer be accommodated after the deadline.
            </div>

            <hr />

            {/* ── Section 2 ── */}
            <h3>Relief for Children Born to Foreign Parents</h3>

            <h4>What's eased</h4>
            <p>
              The amended rules also provide relief for families where a child
              is born in India and at least one parent is an Indian citizen.
              Previously, parents were required to electronically notify
              registration authorities within 30 days of the child's birth to
              access visa-related services. Under the new provisions, this
              requirement has been waived if either parent is an Indian citizen
              and intends to retain the child's Indian citizenship.
            </p>
            <p>
              However, if the child later acquires foreign citizenship while
              residing in India, either parent must inform the registration
              officer within 30 days of acquiring that citizenship.
            </p>

            <hr />

            {/* ── Section 3 ── */}
            <h3>Updated Reporting Requirements for Hospitals</h3>

            <p>
              The notification also revises reporting obligations for hospitals,
              nursing homes, and other medical institutions that provide
              accommodation or overnight facilities to foreign nationals. The
              changes are aimed at improving compliance, strengthening
              administrative oversight, and ensuring better tracking of foreign
              visitors receiving medical treatment or lodging in India.
            </p>

            {/* ── Image 2 ── */}
            <img
              src="https://digitalgoa.com/wp-content/uploads/2025/04/FRRO.jpg"
              alt="India immigration documents and passport"
              onError={(e) => {
                e.target.src =
                  "https://images.unsplash.com/photo-1521791055366-0d553872952f?w=800&q=80";
                e.target.onerror = null;
              }}
              style={{
                width: "100%",
                height: "240px",
                objectFit: "cover",
                borderRadius: "10px",
                margin: "20px 0 8px",
                border: "1px solid #dde2ea",
                display: "block",
              }}
            />
            <p
              style={{
                fontSize: "12px",
                color: "#999",
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              Foreign nationals must complete registration formalities well
              before the 180-day stay limit under the new rules
            </p>

            <hr />

            {/* ── Section 4 ── */}
            <h3>How the Rules Changed: A Timeline</h3>

            {timelineItems.map((item, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: "16px",
                  marginBottom: "20px",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    minWidth: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    background: "#1a3a1a",
                    marginTop: "6px",
                  }}
                />
                <div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "#1a3a1a",
                      marginBottom: "4px",
                    }}
                  >
                    {item.label}
                  </div>
                  <div
                    style={{
                      fontSize: "15px",
                      lineHeight: 1.65,
                      color: "#333",
                    }}
                  >
                    {item.text}
                  </div>
                </div>
              </div>
            ))}

            <hr />

            {/* ── Section 5 ── */}
            <h3>Impact on Foreign Visitors</h3>

            <p>
              The amendments are expected to tighten monitoring of foreign
              nationals' stays while simplifying procedures involving Indian
              citizenship cases. Authorities have advised foreign visitors,
              students, professionals, and long-term residents planning to
              remain in India beyond 180 days to complete registration well in
              advance of the deadline to avoid penalties, visa complications, or
              legal issues.
            </p>
            <p>
              The move forms part of the government's broader efforts to
              strengthen immigration management, improve compliance monitoring,
              and streamline foreigner registration procedures across the
              country.
            </p>

            <hr />

            {/* ── Summary ── */}
            <h3>The Bottom Line</h3>

            <ul>
              <li>
                The 14-day grace period after 180 days is <strong>gone</strong>{" "}
                — registration must happen before the clock runs out
              </li>
              <li>
                Visa holders with per-stay caps of 180 days are equally affected
                regardless of total visa validity
              </li>
              <li>
                Families with one Indian citizen parent get meaningful relief on
                child birth-notification requirements
              </li>
              <li>
                Hospitals and medical facilities face tightened reporting
                obligations for foreign national guests
              </li>
              <li>
                Only genuine emergencies will be considered for overstay
                approvals going forward
              </li>
            </ul>

            <p>
              Foreign nationals are strongly advised to track their 180-day stay
              window carefully and initiate FRRO registration well before the
              deadline to avoid penalties or visa complications.
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default IndiaForeignerRegistration;
